/**
 * ADR-028 E3's live application: the evaluator's crease weights, written onto
 * the meshes that carry the targets.
 *
 * Extracted from the frame driver deliberately. E1's and E2's application
 * lives inside a closure in main.ts and NO test exercises it — the elbow
 * probes replicate the door's arithmetic instead of calling it, so the real
 * door is free to drift from its own probe. The crease does not inherit that:
 * this is the code the driver actually runs, and it is the code the tests
 * actually call.
 *
 * Structural types, no three import — the engine-free side of the layer gate
 * (ADR-002 §6). An influence is a number in an array; nothing here needs to
 * know what a mesh is.
 */

/** One mesh's slot for a crease target. */
export interface CreaseTarget {
  readonly influences: number[];
  readonly index: number;
}

export interface CreaseDrive {
  /** The evaluator output name this drive answers to. */
  readonly name: string;
  /**
   * Every mesh carrying the target. A garment skinned with copied body weights
   * carries the body's crease, so one declared morph drives several meshes —
   * and they must move together or the garment slides off the fold.
   */
  readonly targets: readonly CreaseTarget[];
}

export interface CreaseWeight {
  readonly name: string;
  readonly weight: number;
}

/**
 * Write each evaluated weight to its targets.
 *
 * `drives` is INDEX-ALIGNED with the evaluator's output, because both are
 * built from `profile.morphs` in order. That makes this an index walk rather
 * than a name lookup per morph per hinge per frame — the driver is meant to be
 * free (ADR-028 D5's runtime note), and a `find` in a per-frame loop is
 * exactly the cost that stops being free in a crowd.
 *
 * The alignment is an invariant, so it is CHECKED rather than assumed: if the
 * evaluator ever changes shape, driving the wrong target would be silent and
 * wrong, and a crease firing on the wrong ramp is precisely the defect nobody
 * can diagnose from the outside.
 */
export function applyCreaseWeights(drives: readonly CreaseDrive[], weights: readonly CreaseWeight[]): void {
  if (drives.length !== weights.length) {
    throw new Error(
      `crease drives misaligned: the evaluator returned ${weights.length} weight(s), the mount holds ` +
        `${drives.length} drive(s) — the profile and the drives disagree (§8, fail loud)`,
    );
  }
  for (let i = 0; i < weights.length; i += 1) {
    const weight = weights[i]!;
    const drive = drives[i]!;
    if (drive.name !== weight.name) {
      throw new Error(
        `crease drive misaligned at ${i}: evaluator said "${weight.name}", mount holds "${drive.name}" — ` +
          `the profile and the drives disagree (§8, fail loud)`,
      );
    }
    for (const t of drive.targets) t.influences[t.index] = weight.weight;
  }
}
